import { Controller } from "@hotwired/stimulus";

const KLIPY_BASE = "https://api.klipy.com/api/v1";
const PER_PAGE = 24;

export default class extends Controller {
  static targets = ["search", "grid", "preview", "input", "panel"];
  static values = {
    fieldName: String,
    customerId: String,
    locale: { type: String, default: "de" },
    open: Boolean,
  };

  connect() {
    this.selected = [];
    this.boundReset = this.handleSubmitEnd.bind(this);
    document.addEventListener("turbo:submit-end", this.boundReset);
    this.boundImgLoad = this.handleImgLoad.bind(this);
    this.element.addEventListener("load", this.boundImgLoad, true);
    if (this.hasPanelTarget) {
      this.boundOnClose = () => {
        this.openValue = false;
      };
      this.panelTarget.addEventListener("close", this.boundOnClose);
    }
    this.hydrateExisting();
  }

  disconnect() {
    document.removeEventListener("turbo:submit-end", this.boundReset);
    this.element.removeEventListener("load", this.boundImgLoad, true);
    if (this.hasPanelTarget && this.boundOnClose) {
      this.panelTarget.removeEventListener("close", this.boundOnClose);
    }
    this.scrollObserver?.disconnect();
  }

  handleImgLoad(event) {
    if (
      event.target.tagName === "IMG" &&
      this.gridTarget.contains(event.target)
    ) {
      event.target.classList.add("is-loaded");
    }
  }

  open() {
    if (!this.apiKey) {
      console.warn("VITE_KLIPY_API_KEY is not set; GIF picker disabled");
      return;
    }
    this.openValue = true;
    if (this.hasPanelTarget) {
      this.panelTarget.showModal();
    }
    if (!this.gridLoaded) {
      this.loadTrending();
      this.gridLoaded = true;
    }
  }

  close() {
    this.openValue = false;
    this.hasPanelTarget && this.panelTarget.close();
  }

  hydrateExisting() {
    if (!this.hasInputTarget) return;
    this.inputTarget.querySelectorAll("input[type=hidden]").forEach((input) => {
      const id = input.dataset.gifId || this.#generateId();
      input.dataset.gifId = id;
      this.selected.push({ id, url: input.value });

      // Build a preview tile when one wasn't server-rendered. The post form
      // shares the dropzone container, which cannot hold pre-rendered tiles,
      // so existing GIFs are rendered here from the persisted hidden inputs.
      if (
        this.hasPreviewTarget &&
        !this.previewTarget.querySelector(
          `.comment-attachment-preview[data-gif-id="${id}"]`
        )
      ) {
        this.previewTarget.appendChild(this.#buildPreviewNode(id, input.value));
      }
    });
    if (this.selected.length > 0 && this.hasPreviewTarget) {
      this.previewTarget.classList.add("has-files");
    }
  }

  loadTrending() {
    this.#startSession(null);
    this.#fetchPage();
  }

  search() {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => this.runSearch(), 300);
  }

  runSearch() {
    const q = this.searchTarget.value.trim();
    if (!q) return this.loadTrending();
    this.#startSession(q);
    this.#fetchPage();
  }

  select(event) {
    const button = event.currentTarget;
    const url = button.dataset.gifUrl;
    const thumb = button.dataset.gifThumb;
    this.addSelection(url, thumb);
    this.dispatchChange();
    this.element.dispatchEvent(new CustomEvent("gif-picker:selected", {
      bubbles: true,
      detail: { url, thumb }
    }));
    this.close();
  }

  addSelection(url, thumb) {
    const id = this.#generateId();
    this.selected.push({ id, url });

    if (this.hasPreviewTarget) {
      this.previewTarget.appendChild(this.#buildPreviewNode(id, thumb));
      this.previewTarget.classList.add("has-files");
    }

    if (this.hasInputTarget) {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = this.fieldNameValue;
      input.value = url;
      input.dataset.gifId = id;
      this.inputTarget.appendChild(input);
    }
  }

  #buildPreviewNode(id, src) {
    const previewNode = document.createElement("div");
    previewNode.className = "comment-attachment-preview";
    previewNode.dataset.gifId = id;

    const thumb = document.createElement("div");
    thumb.className = "comment-attachment-preview__thumb";

    // Build with DOM APIs so url/id values are attribute-encoded, never parsed as HTML
    const img = document.createElement("img");
    img.className = "comment-attachment-preview__img";
    img.src = src;
    img.alt = "";
    img.loading = "lazy";
    img.decoding = "async";

    const removeButton = document.createElement("button");
    removeButton.type = "button";
    removeButton.className = "comment-attachment-preview__remove";
    removeButton.dataset.action = "click->gif-picker#remove";
    removeButton.dataset.gifId = id;
    removeButton.setAttribute("aria-label", "Remove");
    // Static markup — safe to inject as HTML
    removeButton.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M18.3 5.7a1 1 0 00-1.4 0L12 10.6 7.1 5.7a1 1 0 00-1.4 1.4L10.6 12l-4.9 4.9a1 1 0 101.4 1.4L12 13.4l4.9 4.9a1 1 0 001.4-1.4L13.4 12l4.9-4.9a1 1 0 000-1.4z" fill="currentColor"/></svg>`;

    thumb.appendChild(img);
    thumb.appendChild(removeButton);
    previewNode.appendChild(thumb);
    return previewNode;
  }

  remove(event) {
    const id = event.currentTarget.dataset.gifId;
    this.selected = this.selected.filter((s) => s.id !== id);
    if (this.hasPreviewTarget) {
      this.previewTarget
        .querySelectorAll(`[data-gif-id="${id}"]`)
        .forEach((n) => n.remove());
      const hasAny =
        this.previewTarget.querySelectorAll(".comment-attachment-preview")
          .length > 0;
      this.previewTarget.classList.toggle("has-files", hasAny);
    }
    if (this.hasInputTarget) {
      this.inputTarget
        .querySelectorAll(`[data-gif-id="${id}"]`)
        .forEach((n) => n.remove());
    }
    this.dispatchChange();
  }

  dispatchChange() {
    const hasAny =
      this.hasPreviewTarget &&
      this.previewTarget.querySelectorAll(".comment-attachment-preview")
        .length > 0;
    this.element.dispatchEvent(
      new CustomEvent("comment-attachment:change", {
        bubbles: true,
        detail: { hasFiles: hasAny },
      }),
    );
  }

  handleSubmitEnd(event) {
    if (!event.detail.success) return;
    // Only reset for our own form's submit — a page can host several composers
    // (e.g. the main comment form plus an open reply form), and clearing on any
    // document-wide submit would wipe GIFs selected in a different composer.
    if (!this.element.contains(event.target)) return;
    this.selected = [];
    if (this.hasPreviewTarget) {
      this.previewTarget
        .querySelectorAll("[data-gif-id]")
        .forEach((n) => n.remove());
    }
    if (this.hasInputTarget) {
      this.inputTarget
        .querySelectorAll("[data-gif-id]")
        .forEach((n) => n.remove());
    }
  }

  #startSession(query) {
    this.currentQuery = query;
    this.currentPage = 1;
    this.hasNext = true;
    this.loading = false;
    this.columnRatios = [0, 0];
    this.gridTarget.innerHTML = `
      <div class="gif-picker__grid-columns">
        <div class="gif-picker__grid-column"></div>
        <div class="gif-picker__grid-column"></div>
      </div>
      <div class="gif-picker__grid-sentinel"></div>
    `;
    this.columnElements = this.gridTarget.querySelectorAll(
      ".gif-picker__grid-column",
    );
    this.#setupScrollObserver();
  }

  #setupScrollObserver() {
    this.scrollObserver?.disconnect();
    const sentinel = this.gridTarget.querySelector(
      ".gif-picker__grid-sentinel",
    );
    if (!sentinel) return;
    this.scrollObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !this.loading && this.hasNext) {
          this.#fetchPage();
        }
      },
      { root: this.gridTarget, rootMargin: "200px" },
    );
    this.scrollObserver.observe(sentinel);
  }

  async #fetchPage() {
    if (!this.apiKey || this.loading || !this.hasNext) return;
    this.loading = true;
    const isFirstPage = this.currentPage === 1;
    if (isFirstPage) this.gridTarget.classList.add("is-loading");
    try {
      const action = this.currentQuery ? "search" : "trending";
      const res = await fetch(
        this.#endpoint(action, {
          page: this.currentPage,
          q: this.currentQuery,
        }),
      );
      const json = await res.json();
      this.#appendItems(json.data?.data || []);
      this.hasNext = !!json.data?.has_next;
      this.currentPage += 1;
    } catch (err) {
      console.warn(`Klipy fetch failed`, err);
    } finally {
      this.loading = false;
      this.gridTarget.classList.remove("is-loading");
    }
  }

  #appendItems(items) {
    for (const item of items) {
      const original = item.file?.hd?.gif?.url;
      const thumb = item.file?.sm?.gif?.url;
      const width = item.file?.sm?.gif?.width || 1;
      const height = item.file?.sm?.gif?.height || 1;
      const blurPreview = item.blur_preview;
      if (!original || !thumb) continue;

      const shorter = this.columnRatios[0] <= this.columnRatios[1] ? 0 : 1;

      // Build with DOM APIs so KLIPY-supplied url/title values are safely
      // encoded as attributes/CSS values instead of parsed as HTML (XSS).
      const button = document.createElement("button");
      button.type = "button";
      button.style.aspectRatio = `${width} / ${height}`;
      // blurPreview is a KLIPY-supplied blur-hash data URI. Only honor it when it
      // is actually a data: URI so an unexpected value can't be smuggled into CSS.
      if (blurPreview && /^data:image\//.test(blurPreview)) {
        button.style.backgroundImage = `url("${blurPreview}")`;
      }
      button.dataset.action = "click->gif-picker#select";
      button.dataset.gifUrl = original;
      button.dataset.gifThumb = thumb;
      button.setAttribute("aria-label", item.title || "GIF");

      const img = document.createElement("img");
      img.src = thumb;
      img.alt = "";
      img.loading = "lazy";
      button.appendChild(img);

      this.columnElements[shorter].appendChild(button);
      this.columnRatios[shorter] += height / width;
    }
  }

  #endpoint(action, { page = 1, q = null } = {}) {
    const params = new URLSearchParams({
      page: String(page),
      per_page: String(PER_PAGE),
      customer_id: this.customerIdValue,
      locale: this.localeValue,
    });
    if (q) params.set("q", q);
    return `${KLIPY_BASE}/${this.apiKey}/gifs/${action}?${params.toString()}`;
  }

  // Just pairs a preview tile with its hidden input — no crypto strength needed.
  // Avoids crypto.randomUUID(), which is undefined in non-secure contexts and
  // older webviews (e.g. Hotwire Native / LAN http).
  #generateId() {
    return `gif-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }

  get apiKey() {
    return import.meta.env.VITE_KLIPY_API_KEY;
  }
}
